/* Le menu : ce qu'un agent voit, et ce que la direction voit en plus.
 *
 * Les entrées sont des données, pas du JSX. C'est ce qui permet de les
 * éprouver sans navigateur : le rail les met en forme, il ne les choisit pas.
 *
 * Périmètre V1, décidé par Aymeric le 1er novembre 2026 : cinq entrées pour
 * un agent — Ventes, Catalogue, Clients, Usines, Commissions — et cinq de
 * plus pour la direction — Prix, Agents, Facturation, Import, Modèles. Les
 * domaines sortis de la V1 ne sont pas supprimés : leur code vit dans
 * `src/v2/`, leurs routes et leurs entrées de menu sont retirées. */

import type { NomIcone } from "@/ui/Icon";
import type { NiveauAgent } from "@/types/metier";
import type { Compteurs } from "@/lib/compteurs";

export interface Entree {
  cle: string;
  nom: string;
  icone: NomIcone;
  niveau: NiveauAgent;
  /** Ce qui attend quelque chose derrière cette entrée, s'il y a lieu. */
  compteur?: keyof Compteurs;
}

export interface Groupe {
  label: string | null;
  entrees: Entree[];
}

/* Le rail suit le travail : on vend, et on s'appuie sur le carnet. La
   direction a son groupe, qu'un agent ne voit pas. Pas de groupe à un seul
   élément : le titre coûterait une ligne sans rien apprendre. */

export const GROUPES: Groupe[] = [
  {
    label: "Vendre",
    entrees: [
      { cle: "ventes", nom: "Ventes", icone: "ventes", niveau: "agent" },
      { cle: "commissions", nom: "Commissions", icone: "commissions", niveau: "agent",
        compteur: "commissions" },
    ],
  },
  {
    label: "Carnet",
    entrees: [
      { cle: "catalogue", nom: "Catalogue", icone: "catalogue", niveau: "agent" },
      { cle: "clients", nom: "Clients", icone: "clients", niveau: "agent" },
      { cle: "usines", nom: "Usines", icone: "usines", niveau: "agent" },
    ],
  },
  {
    label: "Direction",
    entrees: [
      { cle: "prix", nom: "Prix", icone: "listes", niveau: "admin" },
      { cle: "agents", nom: "Agents", icone: "agents", niveau: "admin" },
      { cle: "facturation", nom: "Facturation", icone: "facturation", niveau: "admin",
        compteur: "commissions" },
      { cle: "import", nom: "Import", icone: "importer", niveau: "admin" },
      { cle: "modeles", nom: "Modèles de document", icone: "modeles", niveau: "admin" },
    ],
  },
];

/* Les onglets du bas sont le menu de l'agent, au pouce : ses cinq entrées,
   dans l'ordre où son métier les lui demande. Cinq onglets tiennent à 390 px ;
   un sixième ne tiendrait pas, et c'est pourquoi « Plus » n'apparaît que
   lorsqu'un niveau a des entrées au-delà des onglets. */

export const ONGLETS: Entree[] = [
  { cle: "ventes", nom: "Ventes", icone: "ventes", niveau: "agent" },
  { cle: "catalogue", nom: "Catalogue", icone: "catalogue", niveau: "agent" },
  { cle: "clients", nom: "Clients", icone: "clients", niveau: "agent" },
  { cle: "usines", nom: "Usines", icone: "usines", niveau: "agent" },
  { cle: "commissions", nom: "Commissions", icone: "commissions", niveau: "agent" },
];

/* Les écrans qui ne sont pas des entrées du rail portent quand même un nom. */
export const HORS_RAIL: Record<string, string> = {
  "/devis": "Nouveau devis",
  "/listes": "Listes de prix",
};

/* Le titre suit la section, fiche ouverte ou non : /ventes/<id> reste « Ventes ». */
export function titreDe(chemin: string): string {
  const racine = `/${chemin.split("/")[1] ?? ""}`;
  if (racine === "/catalogue" && chemin.includes("/listes")) return "Listes de prix";
  for (const g of GROUPES) {
    for (const e of g.entrees) if (`/${e.cle}` === racine) return e.nom;
  }
  return HORS_RAIL[racine] ?? "Omnix Desk";
}
